import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import {
  StyledTable,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';
import type { PendingGroupProposalWithSignatures } from '@/types/group-proposal';
import {
  SectionLeftColumn,
  SectionTitle,
  SectionDescription,
  SectionComparisonBeforeColumn,
  SectionComparisonAfterColumn,
} from '@/components/shared/SectionComponents';
import { ProposalActionButtons } from '@/components/ui/proposal-action-buttons';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import { useGroupProposalSignature } from '@/hooks/admin/group/use-group-proposal-signature';
import { useExecuteGroupProposal } from '@/hooks/admin/group/use-execute-group-proposal';
import { useAuthClient } from '@/lib/authClient';
import { formatName } from '@/lib/name';
import { ChangeBadge } from '@/components/ui/change-badge';
import { getChangeBadges } from '@/types/group-changes';
import { MemberDisplay } from '@/components/members/member-display';
import { cn } from '@/lib/utils';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { useProposalExecutionState } from '@/components/execution/use-proposal-execution-state';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { usePendingGroupProposals } from '@/hooks/admin/group/use-pending-group-proposals';
import { KeyedMutator } from 'swr';
import {
  getSignatureCountsAndUserVote,
  canExecuteApproval,
  canExecuteRejection as canExecuteRejectionForProposal,
} from '@/utils/SignatureCountsHelpers';
import {
  ExecuteAction,
  getInvalidProposalDisplayMessage,
  OrgOperationDecision,
} from '@mls-wallet/shared-types';

interface GroupChangeDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposedChange: PendingGroupProposalWithSignatures;
  onChangeUpdated: () => void;
  onClose: () => void;
  refreshProposals: KeyedMutator<PendingGroupProposalWithSignatures[]>;
  proposals: PendingGroupProposalWithSignatures[];
}

// Component for displaying values with change highlighting
const ComparisonValue = ({ children }: { children: React.ReactNode }) => {
  const baseClasses = 'text-sm font-medium min-h-[40px] flex items-center';
  return <div className={baseClasses}>{children}</div>;
};

export function GroupChangeDetailDialog({
  open,
  onOpenChange,
  proposedChange,
  onChangeUpdated,
  onClose,
  refreshProposals,
  proposals,
}: GroupChangeDetailDialogProps) {
  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  const { createSignature, isLoading: isSignatureLoading } =
    useGroupProposalSignature();
  const { user } = useAuthClient();

  const handleApprove = async () => {
    try {
      // Create approval signature
      await createSignature({
        proposalId: proposedChange.proposal.id,
        decision: OrgOperationDecision.APPROVE,
      });
      // Refresh proposal so the frontend knows the signature record has been created, but proposal hasn't been actually signed yet
      // This way, we can close the modal when either the proposal is signed (confirmed) or the signature record is deleted (cancelled)
      await refreshProposals();
      // Open the pending signatures modal
      setPendingSignableIds([proposedChange.proposal.id]);
      setPendingDecision(OrgOperationDecision.APPROVE);
      setIsPendingModalOpen(true);
    } catch (error) {
      console.error('Failed to approve proposal', error);
      resetPendingState();
    }
  };

  const handleReject = async () => {
    try {
      // Create rejection signature
      await createSignature({
        proposalId: proposedChange.proposal.id,
        decision: OrgOperationDecision.REJECT,
      });
      // Refresh proposal so the frontend knows the signature record has been created, but proposal hasn't been actually signed yet
      // This way, we can close the modal when either the proposal is signed (confirmed) or the signature record is deleted (cancelled)
      await refreshProposals();
      // Open the pending signatures modal
      setPendingSignableIds([proposedChange.proposal.id]);
      setPendingDecision(OrgOperationDecision.REJECT);
      setIsPendingModalOpen(true);
    } catch (error) {
      console.error('Failed to reject proposal', error);
      resetPendingState();
    }
  };

  const { executeProposal, isLoading: isExecuteLoading } =
    useExecuteGroupProposal();
  const proposalId = proposedChange.proposal.id;
  const { isExecuting: isProposalExecuting, startExecution } =
    useProposalExecutionState({
      proposalId,
      isServerExecuting: proposedChange.isExecuting,
      isRequestPending: isExecuteLoading,
    });

  /**
   * Handle execute button click for both approval and rejection.
   * Both APPROVE and REJECT create a GuardianTransaction and are processed via
   * the async worker. The modal polls until completion and handles callbacks.
   */
  const handleExecute = (action: ExecuteAction) => {
    // Case: an active proposal execution blocks a duplicate Guardian request.
    if (isProposalExecuting) return;
    // Case: execution requires the corresponding approval or rejection threshold.
    if (action === 'APPROVE' && !canExecute) return;
    if (action === 'REJECT' && !canExecuteRejection) return;

    startExecution({
      executionTarget: 'proposal',
      executionTargetId: proposalId,
      proposalType: 'group',
      request: () => executeProposal({ proposalId, action }),
      onAlreadyExecuting: async () => {
        await refreshProposals();
      },
      onSuccess: async () => {
        onChangeUpdated();
        onClose();
        await refreshProposals();
      },
    });
  };

  const { numApprovals, numRejections, hasApproved, hasRejected } = useMemo(
    () =>
      getSignatureCountsAndUserVote({
        approvals: proposedChange.approvals,
        rejections: proposedChange.rejections,
        userId: user?.id,
        requiredNetworkIds: proposedChange.requiredNetworkIds,
      }),
    [
      proposedChange.approvals,
      proposedChange.rejections,
      proposedChange.requiredNetworkIds,
      user?.id,
    ]
  );

  // Check if proposal can be executed (approval)
  const canExecute =
    canExecuteApproval({
      status: proposedChange.proposal.status,
      numApprovals,
      requiredApprovals: proposedChange.requiredApprovals,
    }) &&
    (proposedChange.proposal.action === 'CREATE'
      ? !proposedChange.proposal.groupId
      : !!proposedChange.proposal.groupId);

  // Check if proposal can be rejected (enough rejections)
  const canExecuteRejection = canExecuteRejectionForProposal({
    status: proposedChange.proposal.status,
    numRejections,
    requiredApprovals: proposedChange.requiredApprovals,
  });

  const approveDisabledReason =
    proposedChange.proposal.validity === 'valid'
      ? undefined
      : getInvalidProposalDisplayMessage(
          proposedChange.proposal.domainInvalidReason
        );

  const footer = isSignatureLoading ? (
    <div className="flex justify-center py-4">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  ) : (
    <ProposalActionButtons
      onReject={handleReject}
      onApprove={handleApprove}
      onExecuteRejection={() => handleExecute('REJECT')}
      onExecute={() => handleExecute('APPROVE')}
      hasApproved={hasApproved}
      hasRejected={hasRejected}
      canExecuteRejection={canExecuteRejection}
      canExecute={canExecute}
      isExecuting={isProposalExecuting}
      approveDisabled={proposedChange.proposal.validity !== 'valid'}
      approveDisabledReason={approveDisabledReason}
    />
  );

  const membersChanged =
    proposedChange.proposal.groupMemberProposals.length > 0;

  // Compute before/after members based on action type
  const currentMembers = proposedChange.proposal.group?.members ?? [];
  const addedMembers = proposedChange.proposal.groupMemberProposals.filter(
    (m) => m.action === 'ADD'
  );
  const removedMembers = proposedChange.proposal.groupMemberProposals.filter(
    (m) => m.action === 'REMOVE'
  );

  // Before members
  const beforeMembers =
    proposedChange.proposal.action === 'CREATE' ? [] : currentMembers;

  // After members (always MinimalUser[] for consistency)
  let afterMembers: typeof currentMembers;
  if (proposedChange.proposal.action === 'DELETE') {
    afterMembers = []; // Group will be deleted
  } else if (proposedChange.proposal.action === 'CREATE') {
    // Only added members for new group (extract .user from GroupMemberProposal)
    afterMembers = addedMembers.map((m) => m.user);
  } else {
    // UPDATE: Start with current, remove REMOVE actions, add ADD actions
    const removedIds = new Set(removedMembers.map((m) => m.user.id));
    const currentAfterRemovals = currentMembers.filter(
      (m) => !removedIds.has(m.id)
    );
    const newMembers = addedMembers.map((m) => m.user);
    afterMembers = [...currentAfterRemovals, ...newMembers];
  }

  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        proposedChange.proposal.action === 'CREATE'
          ? 'Proposed group creation'
          : `Proposed changes to ${proposedChange.proposal.group?.name ?? '(Unnamed)'}`
      }
      footer={footer}
      bodyContainerClassName="bg-muted"
    >
      <div className="p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Summary Section */}
          <div className="space-y-4">
            {/* Change Badges */}
            <div className="flex flex-wrap gap-1.5">
              {getChangeBadges(proposedChange.proposal).map((badge, index) => (
                <ChangeBadge
                  key={index}
                  changeType={badge.changeType}
                  text={badge.text}
                />
              ))}
            </div>

            {/* Metadata Bar */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center divide-x divide-border">
                <div className="pr-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Proposed by
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {formatName(proposedChange.proposedBy.name)}
                  </span>
                </div>
                <div className="px-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Proposed on
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {new Date(
                      proposedChange.proposal.createdAt
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <div className="px-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Approvals
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {numApprovals} of {proposedChange.requiredApprovals}
                  </span>
                </div>
                <div className="pl-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Rejections
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">{numRejections}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Group Details Tables */}
          {proposedChange.proposal.action === 'CREATE' ? (
            <StyledTable style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '280px' }} />
                <col />
              </colgroup>
              <StyledTableBody>
                <StyledTableRow>
                  <StyledTableCell className="!p-0 vertical-align-stretch">
                    <SectionLeftColumn>
                      <SectionTitle>Group name</SectionTitle>
                      <SectionDescription>
                        The name of this group
                      </SectionDescription>
                    </SectionLeftColumn>
                  </StyledTableCell>
                  <StyledTableCell className="py-4 px-6 vertical-align-stretch">
                    <ComparisonValue>
                      <div className="p-2 w-full">
                        <span className="text-sm font-medium">
                          {proposedChange.proposal.groupName || '(Unnamed)'}
                        </span>
                      </div>
                    </ComparisonValue>
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell className="!p-0 vertical-align-stretch">
                    <SectionLeftColumn>
                      <SectionTitle>Group members</SectionTitle>
                      <SectionDescription>
                        Members who will be added to this group
                      </SectionDescription>
                    </SectionLeftColumn>
                  </StyledTableCell>
                  <StyledTableCell className="py-4 px-6 vertical-align-stretch">
                    <ComparisonValue>
                      <div className="space-y-2 w-full">
                        {proposedChange.proposal.groupMemberProposals.length >
                        0 ? (
                          proposedChange.proposal.groupMemberProposals
                            .filter((m) => m.action === 'ADD')
                            .map((member) => (
                              <MemberDisplay
                                key={member.user.id}
                                member={member.user}
                                size="sm"
                                variant="list"
                                className="p-2 w-full"
                              />
                            ))
                        ) : (
                          <span className="text-muted-foreground">
                            No members
                          </span>
                        )}
                      </div>
                    </ComparisonValue>
                  </StyledTableCell>
                </StyledTableRow>
              </StyledTableBody>
            </StyledTable>
          ) : (
            <StyledTable style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '280px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '180px' }} />
              </colgroup>
              <StyledTableHeader>
                <StyledTableRow>
                  <StyledTableHead></StyledTableHead>
                  <StyledTableHead>Before</StyledTableHead>
                  <StyledTableHead>After</StyledTableHead>
                </StyledTableRow>
              </StyledTableHeader>
              <StyledTableBody>
                {proposedChange.proposal.action === 'DELETE' ? (
                  <StyledTableRow>
                    <StyledTableCell className="!p-0 vertical-align-stretch">
                      <SectionLeftColumn>
                        <SectionTitle>Group name</SectionTitle>
                        <SectionDescription>
                          The group that will be removed
                        </SectionDescription>
                      </SectionLeftColumn>
                    </StyledTableCell>
                    <StyledTableCell className="p-0 vertical-align-stretch">
                      <SectionComparisonBeforeColumn highlight={true}>
                        <ComparisonValue>
                          <div className="rounded-md border border-red-300 bg-red-100 p-2 w-full dark:border-red-700 dark:bg-red-900/30">
                            <span className="text-sm font-medium text-red-700 line-through dark:text-red-400">
                              {proposedChange.proposal.group?.name ??
                                '(Unnamed)'}
                            </span>
                          </div>
                        </ComparisonValue>
                      </SectionComparisonBeforeColumn>
                    </StyledTableCell>
                    <StyledTableCell className="!p-0 vertical-align-stretch">
                      <SectionComparisonAfterColumn highlight={false}>
                        <ComparisonValue>
                          <div className="p-2 rounded-md w-full">
                            <span className="text-sm font-medium">—</span>
                          </div>
                        </ComparisonValue>
                      </SectionComparisonAfterColumn>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : null}
                <StyledTableRow>
                  <StyledTableCell className="!p-0 vertical-align-stretch !border-right border-border">
                    <SectionLeftColumn>
                      <SectionTitle>Group members</SectionTitle>
                      <SectionDescription>
                        Members who belong to this group
                      </SectionDescription>
                    </SectionLeftColumn>
                  </StyledTableCell>
                  <StyledTableCell className="!p-0 align-top">
                    <div className="flex flex-row items-stretch justify-stretch w-full h-full">
                      <SectionComparisonBeforeColumn
                        highlight={
                          !!membersChanged ||
                          proposedChange.proposal.action === 'DELETE'
                        }
                      >
                        <ComparisonValue>
                          <div className="space-y-2 w-full">
                            {beforeMembers.length > 0 ? (
                              beforeMembers.map((member) => {
                                // For DELETE proposals, all members are being removed
                                const isBeingRemoved =
                                  proposedChange.proposal.action === 'DELETE' ||
                                  removedMembers.some(
                                    (rm) => rm.user.id === member.id
                                  );
                                return (
                                  <MemberDisplay
                                    key={member.id}
                                    member={member}
                                    size="sm"
                                    variant="list"
                                    className={cn(
                                      'p-2 w-full',
                                      isBeingRemoved &&
                                        'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700'
                                    )}
                                    textClassName={cn(
                                      isBeingRemoved &&
                                        'line-through text-red-700 dark:text-red-400'
                                    )}
                                  />
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground">
                                No members
                              </span>
                            )}
                          </div>
                        </ComparisonValue>
                      </SectionComparisonBeforeColumn>
                    </div>
                  </StyledTableCell>
                  <StyledTableCell className="!p-0 align-top">
                    <SectionComparisonAfterColumn
                      highlight={
                        !!membersChanged &&
                        proposedChange.proposal.action !== 'DELETE'
                      }
                    >
                      <ComparisonValue>
                        <div className="space-y-2 w-full">
                          {proposedChange.proposal.action === 'DELETE' ? (
                            <span className="text-muted-foreground">—</span>
                          ) : afterMembers.length > 0 ? (
                            afterMembers.map((member) => {
                              const isBeingAdded = addedMembers.some(
                                (am) => am.user.id === member.id
                              );
                              return (
                                <MemberDisplay
                                  key={member.id}
                                  member={member}
                                  size="sm"
                                  variant="list"
                                  className={cn(
                                    'p-2 w-full',
                                    isBeingAdded &&
                                      'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700'
                                  )}
                                  textClassName={cn(
                                    isBeingAdded &&
                                      'text-green-700 dark:text-green-400 font-semibold'
                                  )}
                                />
                              );
                            })
                          ) : (
                            <span className="text-muted-foreground">
                              No members
                            </span>
                          )}
                        </div>
                      </ComparisonValue>
                    </SectionComparisonAfterColumn>
                  </StyledTableCell>
                </StyledTableRow>
              </StyledTableBody>
            </StyledTable>
          )}
        </div>
      </div>

      {/* Pending Signature Modal */}
      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refreshProposals}
        pendingSignableIds={pendingSignableIds}
        polledSignables={proposals.map((p) => ({
          id: p.proposal.id,
          approvals: p.approvals,
          rejections: p.rejections,
          requiredApprovals: p.requiredApprovals,
          initiatorSignature: undefined,
        }))}
        pendingDecision={pendingDecision}
        hookFunctionToPoll={usePendingGroupProposals}
        isTransaction={false}
      />
    </NestedFullscreenDialog>
  );
}
